"use client";

import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { StandardFormInput, StandardFormSelect } from "@/components/common/StandardFormInput";
import FormFieldWrapper from "@/components/coupon/formFieldWrapper";
import { Category, CategoryStatus, CategoryAsset, Asset } from "@/types/models/category";
import { Product } from "@/types/models/product";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/store";
import { CategoryFormReset, submitCategoryRequest } from "@/store/slices/category.slice";
import { apiClient } from "@/lib/apiClient";
import ImageUpload from "@/components/ImageUpload/ImageUpload";
import { toast } from "sonner";
import { uploadImageAction } from "@/store/actions/imageupload.actions";
import QuillEditor from "@/components/product/QuillEditor";
import ProductSelector from "@/components/common/ProductSelector";
import { FileManagerModal } from "@/components/fileManager/FileManagerModal";
import { FileManagerConfig, FileSystemItem } from "@/types/fileManager";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { useGlobalLoading } from "@/hooks/useGlobalLoading";

interface MetafieldValue { key: string; type: string; value: string | number | string[] }

export default function NewCategory() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("q");
  const params = useParams();
  const navigate = useNavigate();
  const { storeType, storeId } = params as any;

  const [image, setImage] = useState<string | null>(null); // Single asset ID for backend
  const [asset, setAsset] = useState<CategoryAsset | null>(null); // Single asset object for preview
  const [status, setStatus] = useState<CategoryStatus>("ACTIVE");
  const [description, setDescription] = useState<any>({ ops: [] });
  const [descriptionEditorKey, setDescriptionEditorKey] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  /** Pass-through from collection GET `products[]` so ProductSelector can show titles before opening */
  const [selectorSeedProducts, setSelectorSeedProducts] = useState<Product[]>([]);
  const [existingMetafields, setExistingMetafields] = useState<MetafieldValue[]>([]);
  const [showImageManager, setShowImageManager] = useState(false);
  const submitLockRef = useRef(false);

  const dispatch = useAppDispatch();
  const { loading, error, success } = useAppSelector((state: RootState) => state.category.form);
  const { register, handleSubmit, reset, setValue } = useForm<Category>();
  const { showLoading, hideLoading } = useGlobalLoading();

  const HandleImageChange = async (file: File | null, idx?: number) => {
    if (idx !== undefined) {
      // Remove image
      setImage(null);
      setAsset(null);
      return;
    }
    if (!file) return;
    const assetId: string = await uploadImageAction(file, storeId);
    if (!assetId) return;
    
    // Set new image assetId
    setImage(assetId);
    
    // Build CategoryAsset object for the uploaded image
    const newAsset: CategoryAsset = {
      id: `temp-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      deleted_at: null,
      collection_id: id || "",
      asset_id: assetId,
      position: 0,
      usage: "image",
      asset: {
        id: assetId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        path: `public/store/${storeType}/${storeId}/collections/images/${assetId}`,
        store_id: storeId,
        original_name: file.name,
        size: file.size,
        content_type: file.type,
        usage: "general",
        status: "active",
      },
    };
    
    setAsset(newAsset);
  };

  const handleImageSelect = (selectedFiles: FileSystemItem[]) => {
    if (selectedFiles.length === 0) return;
    
    // Take only the first file since category has single image
    const file = selectedFiles[0];
    
    // Build CategoryAsset object for preview
    const newAsset: CategoryAsset = {
      id: `temp-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      deleted_at: null,
      collection_id: id || "",
      asset_id: file.assetId,
      position: 0,
      usage: "image",
      asset: {
        id: file.assetId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        path: file.path,
        store_id: storeId,
        original_name: file.name,
        size: file.size || 0,
        content_type: file.contentType || file.mimeType || "image/png",
        usage: "general",
        status: "active",
      },
    };
    
    setImage(file.assetId);
    setAsset(newAsset);
    setShowImageManager(false);
  };

  const imageManagerConfig: FileManagerConfig = {
    allowedFileTypes: ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"],
    maxFileSize: 10 * 1024 * 1024,
    maxSelections: 1,
    selectionMode: "single",
    showUpload: true,
    showCreateFolder: true,
    onSelect: handleImageSelect,
    onCancel: () => setShowImageManager(false),
  };

  const onSubmit = async (data: Category) => {
    if (loading || submitLockRef.current) return;
    if (!data.title?.trim()) return toast("Title is required");
    submitLockRef.current = true;
    showLoading(id ? "Updating collection..." : "Creating collection...");
    data.title = data.title.trim();
    data.description = typeof (description as any) === "object" ? JSON.stringify(description) : description;
    data.status = status;
    const filteredExisting = (existingMetafields || []).filter((m) => m && m.key !== "featured_products");
    const toCategoryMetafield = (m: MetafieldValue): Category["metafields"][number] => ({
      key: m.key,
      type: m.type,
      value: Array.isArray(m.value) ? m.value.join(", ") : String(m.value ?? ""),
    });
    data.metafields = [
      ...filteredExisting.map(toCategoryMetafield),
      { key: "featured_products", type: "array", value: JSON.stringify(selectedProducts) },
    ];
    data.imageAssetId = image || undefined; // Use single image assetId
    dispatch(submitCategoryRequest({ data: data, collectionId: id }));
  };

  const parseDescriptionForEditor = (rawDescription: unknown) => {
    if (!rawDescription) return { ops: [] };

    if (typeof rawDescription === "object") {
      return rawDescription;
    }

    if (typeof rawDescription === "string") {
      try {
        return JSON.parse(rawDescription);
      } catch {
        return { ops: [{ insert: rawDescription }] };
      }
    }

    return { ops: [] };
  };

  useEffect(() => {
    if (!id) return;
    const logPrefix = "[CollectionEdit]";
    apiClient.get<Category>(`/api/v1/admin/:storeId/collections/${id}`).then((res) => {
      const raw = res?.data as unknown;
      const collection: Category | undefined =
        Array.isArray(raw) && raw.length > 0
          ? (raw[0] as Category)
          : raw && typeof raw === "object" && !Array.isArray(raw)
            ? (raw as Category)
            : undefined;

      console.log(`${logPrefix} GET /collections/${id} response`, {
        hasData: raw != null,
        dataType: raw === null ? "null" : Array.isArray(raw) ? "array" : typeof raw,
        isArray: Array.isArray(raw),
        unwrappedFromArray: Array.isArray(raw) && raw.length > 0,
        topKeys:
          collection && typeof collection === "object"
            ? Object.keys(collection as object)
            : [],
        productsLength: Array.isArray(collection?.products) ? collection!.products!.length : "n/a",
      });

      if (!collection) {
        console.warn(`${logPrefix} Empty or unparseable collection payload; skipping form hydrate`);
        return;
      }

      // Handle image asset loading
      if (collection?.image_asset) {
        const imageAsset = collection.image_asset;
        
        // Set assetId for submission
        setImage(imageAsset.asset_id);
        
        // Build CategoryAsset object for preview
        const categoryAsset: CategoryAsset = {
          id: imageAsset.id || `loaded-${Date.now()}`,
          created_at: imageAsset.created_at || new Date().toISOString(),
          updated_at: imageAsset.updated_at || new Date().toISOString(),
          deleted_at: imageAsset.deleted_at || null,
          collection_id: id,
          asset_id: imageAsset.asset_id,
          position: 0,
          usage: imageAsset.usage || "image",
          asset: imageAsset.asset || {
            id: imageAsset.asset_id,
            created_at: imageAsset.created_at || new Date().toISOString(),
            updated_at: imageAsset.updated_at || new Date().toISOString(),
            deleted_at: null,
            path: imageAsset.path || "",
            store_id: storeId,
            original_name: imageAsset.name || "",
            size: imageAsset.size || 0,
            content_type: imageAsset.mime_type || "image/png",
            usage: "general",
            status: "active",
          },
        };
        
        setAsset(categoryAsset);
      } else if (collection?.imageAssetId) {
        // Legacy: only assetId available
        setImage(collection.imageAssetId);
        setAsset(null);
      }
      
      setDescription(parseDescriptionForEditor(collection?.description));
      setDescriptionEditorKey((prev) => prev + 1);
      setValue("title", collection?.title);
      if (collection?.status) setStatus(collection.status);
      setExistingMetafields((collection as any)?.metafields || []);
      const seed = Array.isArray(collection?.products) ? collection!.products! : [];
      console.log(`${logPrefix} featured products seed`, {
        seedCount: seed.length,
        seedIds: seed.map((p) => p?.id).filter(Boolean),
      });
      setSelectorSeedProducts(seed);
      const featured = collection?.metafields?.find((i: any) => i?.key === "featured_products");
      let featuredIds: string[] = [];
      if (featured?.value) {
        try {
          const parsed = JSON.parse(featured.value as string);
          featuredIds = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.warn(`${logPrefix} featured_products parse failed`, featured?.value, e);
          featuredIds = [];
        }
      }
      console.log(`${logPrefix} metafield featured_products`, { featuredIds, rawValue: featured?.value });
      setSelectedProducts(featuredIds);
    }).catch((err) => console.error(`${logPrefix} GET collection failed`, err));
  }, [id, setValue, storeId]);

  useEffect(() => { if (error && error.message !== "") { hideLoading(); toast.error(error.message); } }, [error, hideLoading]);
  useEffect(() => {
    if (!loading) {
      submitLockRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    if (success && !loading) {
      hideLoading();
      toast.success(id ? "Collection updated successfully" : "Collection created successfully");
      navigate(`/${storeType}/${storeId}/product/collection`);
      setTimeout(() => { dispatch(CategoryFormReset()); reset(); setStatus("ACTIVE"); setDescription({ ops: [] }); setSelectedProducts([]); setSelectorSeedProducts([]); setImage(null); setAsset(null); }, 100);
    }
  }, [success, loading, dispatch, reset, navigate, storeType, storeId, hideLoading, id]);

  const breadcrumbItems = [ { label: "Products", href: `/${storeType}/${storeId}/product` }, { label: "Collections", href: `/${storeType}/${storeId}/product/collection` }, { label: id ? "Edit Collection" : "Add Collection", isActive: true } ];

  return (
    <div className="space-y-4">
      <Breadcrumb items={breadcrumbItems} showBackButton={false} />
      <form onSubmit={handleSubmit(onSubmit)} className="p-2 lg:p-4 mb-20 space-y-6">
        <FormFieldWrapper title="Collection Information">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <StandardFormInput label="Title" type="text" placeholder="Enter collection name" {...register("title")} />
            <StandardFormSelect label="Status" value={status} onValueChange={(value) => setStatus(value as CategoryStatus)} options={[{ label: "Active", value: "ACTIVE" }, { label: "Draft", value: "DRAFT" }]} placeholder="Select Status" />
          </div>
          <div className="space-y-1">
            <label className="block text-[13px] font-normal text-muted-foreground">Collection Description</label>
            <QuillEditor key={`collection-description-${id || "new"}-${descriptionEditorKey}`} initialValue={description} onChange={setDescription} placeholder="Write collection description..." height={120} />
          </div>
          <ProductSelector key={id || "new"} label="Featured Products" value={selectedProducts} onValueChange={setSelectedProducts} placeholder="Select products to feature in this collection" helperText="Choose products that best represent this collection (max 5)" maxSelections={5} seedProducts={selectorSeedProducts} />
          <div className="space-y-1">
            <label className="block text-[13px] font-normal text-muted-foreground">Collection Image</label>
            <ImageUpload 
              onChange={HandleImageChange} 
              type="single" 
              images={image ? [image] : []} 
              assets={asset ? [asset] : []}
              onOpenFileManager={() => setShowImageManager(true)} 
            />
          </div>
          <div className="flex gap-2 pt-4">
            <ButtonOutline className="rounded-full px-6 border-destructive text-destructive hover:bg-destructive/10" type="button" onClick={() => { reset(); setStatus("ACTIVE"); setDescription({ ops: [] }); setSelectedProducts([]); setSelectorSeedProducts([]); setImage(null); setAsset(null); }}>Clear</ButtonOutline>
            <ButtonPrimary className="rounded-full px-6" type="submit" disabled={loading}>
              {loading
                ? id
                  ? "Updating..."
                  : "Adding..."
                : id
                  ? "Update Collection"
                  : "Add Collection"}
            </ButtonPrimary>
          </div>
        </FormFieldWrapper>
        <FileManagerModal isOpen={showImageManager} onClose={() => setShowImageManager(false)} config={imageManagerConfig} />
      </form>
    </div>
  );
}
